// =============================================================================
// Natural-language quick-add for calendar events.
//
// Wraps `chrono-node` so the calendar can accept input like
//   "Zahnarzt morgen 14:00"
//   "Lunch with Mom Friday 12:30"
//   "Yoga 18:00-19:30"
//   "Geburtstagsfeier Sa 20 Uhr"
// and turn it into the same event shape `Calendar.handleSaveEvent` builds.
//
// Returns `null` if no usable date/time was detected in the input.
// =============================================================================
import * as chrono from 'chrono-node';
import { format } from 'date-fns';

const pad = (n) => String(n).padStart(2, '0');

const fmtDate = (d) => format(d, 'yyyy-MM-dd');
const fmtTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

// Try the German parser first (handles morgen / heute / Mo, Di…), fall back
// to the default English parser. We don't pre-detect language; whichever
// parser finds a result first wins.
const parseWith = (text) => {
  const tries = [
    () => chrono.de.parse(text, new Date(), { forwardDate: true }),
    () => chrono.parse(text, new Date(), { forwardDate: true })
  ];
  for (const fn of tries) {
    try {
      const arr = fn();
      if (arr && arr.length > 0) return arr[0];
    } catch {
      // try next
    }
  }
  return null;
};

/**
 * Parse a free-form quick-add string into a partial event shape.
 *
 *   parseQuickEvent("Zahnarzt morgen 14:00")
 *   → {
 *       title: 'Zahnarzt',
 *       date: '2026-05-08',
 *       allDay: false,
 *       startTime: '14:00',
 *       endTime: '',
 *       endDate: '2026-05-08'
 *     }
 *
 * Returns null when no date/time was detected (so the caller can show the
 * input as "still typing" rather than misclassifying random text).
 */
export function parseQuickEvent(input) {
  const text = (input || '').trim();
  if (!text) return null;
  const result = parseWith(text);
  if (!result) return null;

  const start = result.start ? result.start.date() : null;
  if (!start || Number.isNaN(start.getTime())) return null;
  const end = result.end ? result.end.date() : null;

  // Did the user explicitly include a clock time? chrono encodes that via
  // result.start.isCertain('hour'). Pure-date inputs ("Friday", "morgen")
  // fall back to allDay: true.
  const hasStartTime =
    result.start.isCertain && result.start.isCertain('hour');
  const hasEndTime =
    end && result.end.isCertain && result.end.isCertain('hour');

  // The leftover text after stripping the matched date/time becomes the
  // title. chrono's `result.text` contains the matched span verbatim.
  let title = (text.slice(0, result.index) + text.slice(result.index + result.text.length))
    .replace(/\s+/g, ' ')
    .trim();
  // Drop trailing connector words / punctuation left dangling.
  title = title.replace(/[,;:\-–—]\s*$/, '').replace(/\s+(am|um|on|at)$/i, '').trim();
  if (!title) title = text; // fallback so user always gets *something*

  return {
    title,
    date: fmtDate(start),
    endDate: end ? fmtDate(end) : fmtDate(start),
    allDay: !hasStartTime,
    startTime: hasStartTime ? fmtTime(start) : '',
    endTime: hasEndTime ? fmtTime(end) : '',
    parsedText: result.text
  };
}
